// /en/medical-council/$profession — English detail page
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { MEDICAL_COUNCIL_PROFESSIONS, getMedicalCouncilProfession } from "@/data/medical-council-professions";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { ContactCTA } from "@/components/seo/Blocks";
import { Stethoscope, ShieldCheck, FileCheck2, Clock, MessageCircle, Phone, BadgeCheck, Globe2 } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/medical-council/$profession")({
  loader: ({ params }) => {
    if (!getMedicalCouncilProfession(params.profession)) throw notFound();
    return { slug: params.profession };
  },
  head: ({ params }) => {
    const p = getMedicalCouncilProfession(params.profession);
    if (!p) return { meta: [{ title: ctaTitle("Not Found", "/en/medical-council/$profession", "en")}] };
    const url = `${SITE}/en/medical-council/${p.slug}`;
    const title = `${p.councilEn} Certification for ${p.nameEn} — From THB ${p.priceFromTh.toLocaleString()} | NYC Legal`;
    const desc = `Certificate of Good Standing & verification from ${p.councilEn} for ${p.nameEn}, with certified English translation, Notary, MFA legalisation and embassy attestation. Accepted by ${p.destinations.map((d) => d.authority).join(", ")}. Send your documents for a free check.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/medical-council/$profession", "en") }, { name: "description", content: ctaDesc(desc, "/en/medical-council/$profession", "en")}, { name: "keywords", content: p.keywordsEn.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/en/medical-council/$profession", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/medical-council/$profession", "en")},
        { property: "og:url", content: url }, { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/medical-council/${p.slug}`, `/en/medical-council/${p.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `${p.councilEn} Certification — ${p.nameEn}`,
              serviceType: "Professional Credential Verification",
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", url: SITE, telephone: "+66832494999" },
              areaServed: { "@type": "Country", name: "Thailand" },
              offers: { "@type": "Offer", price: p.priceFromTh, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
              inLanguage: "en",
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "HowTo", "@id": `${url}#howto`,
              name: `How to obtain ${p.councilEn} certification for ${p.nameEn}`,
              totalTime: "P15D",
              step: [
                { "@type": "HowToStep", position: 1, name: "Consultation", text: "Send copies of professional licence and national ID via LINE @nycli; we provide a full quote within 30 minutes" },
                { "@type": "HowToStep", position: 2, name: `${p.councilEn} filing`, text: `We file your Certificate of Good Standing application with ${p.councilEn} under power of attorney` },
                { "@type": "HowToStep", position: 3, name: "Translation", text: "Certified English translation by MoJ-registered translator or NAATI (Australia)" },
                { "@type": "HowToStep", position: 4, name: "Notary + MFA", text: "Notary Public attests the translation; legalised by Department of Consular Affairs, MFA Thailand" },
                { "@type": "HowToStep", position: 5, name: "Embassy + courier", text: "Destination embassy attestation if required, then DHL/FedEx worldwide" },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Medical Council Certification", item: `${SITE}/en/medical-council` },
                { "@type": "ListItem", position: 3, name: p.nameEn, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
          faqPageSchema([
            { q: `How much is ${p.councilEn} certification for ${p.nameEn}?`, a: `Starts THB ${p.priceFromTh.toLocaleString()} including translation and Notary. Council fee (THB 200–1,500), MFA legalisation and destination embassy attestation are quoted separately upfront.` },
            { q: `How long does it take?`, a: `Typical turnaround: ${p.turnaround.replace("วันทำการ", "business days")}. Add 5–10 business days for MFA + embassy attestation. Full status tracking via LINE.` },
            { q: `What documents does ${p.councilEn} issue?`, a: `${p.documents.join("; ")}. All are official certificates accepted by foreign regulators for licensure and examination registration.` },
            { q: `Which countries accept it?`, a: `Primary destinations: ${p.destinations.map((d) => `${d.nameEn} (${d.authority})`).join("; ")}. Other countries available on request.` },
            { q: `Do I need to attend in person?`, a: `No. NYC operates under power of attorney. Send signed authorisation + copy of national ID + professional licence — we handle every step and ship worldwide.` },
            { q: `Can you handle EPIC / direct verification to foreign regulators?`, a: `Yes. We coordinate with the council's international affairs office for direct verification to ECFMG EPIC, CGFNS, AHPRA, GMC and others.` },
          ]),
        ]),
      ],
    };
  },
  component: Page,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Not Found</h1>
      <Link to="/en/medical-council" className="mt-4 inline-block text-gold underline">Back to Medical Council hub</Link>
    </main>
  ),
});

function Page() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const p = getMedicalCouncilProfession(slug)!;
  const related = MEDICAL_COUNCIL_PROFESSIONS.filter((x) => x.slug !== slug).slice(0, 6);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/medical-council" className="hover:text-gold">Medical Council</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{p.nameEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Stethoscope className="h-3.5 w-3.5" /> {p.councilEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            {p.councilEn} Certification for {p.nameEn}
          </h1>
          <p className="mt-2 text-base text-white/80">{p.nameTh} · {p.councilTh}</p>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            Complete credential verification for Thai-trained {p.nameEn.toLowerCase()}s seeking foreign licensure or
            employment abroad — NYC obtains the Certificate of Good Standing from {p.councilEn}, provides certified
            English translation (MoJ / NAATI), Notary Public attestation, MFA legalisation and destination embassy
            attestation. Worldwide DHL / FedEx delivery. Starting at THB {p.priceFromTh.toLocaleString()}.
          </p>
          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "From", v: `THB ${p.priceFromTh.toLocaleString()}` },
              { k: "TAT", v: p.turnaround.replace("วันทำการ", "business days") },
              { k: "Destinations", v: `${p.destinations.length}+` },
              { k: "Rating", v: "4.9 / 2,147" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition">
              <MessageCircle className="h-4 w-4" /> Chat on LINE
            </a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          NYC Legal & Notary Service handles end-to-end {p.councilEn} certification for {p.nameEn} —
          from THB {p.priceFromTh.toLocaleString()}, {p.turnaround.replace("วันทำการ", "business days")}, covering destinations
          {" "}{p.destinations.map((d) => d.nameEn).join(", ")}. Includes certified English translation by MoJ / NAATI,
          Notarial Services Attorney attestation, MFA Thailand legalisation and destination embassy attestation.
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Documents Issued by {p.councilEn}</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {p.documents.map((d) => (
              <li key={d} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {d}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Destination Authorities</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {p.destinations.map((d) => (
              <div key={d.code} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-700">
                  <Globe2 className="h-3 w-3" /> {d.code.toUpperCase()}
                </div>
                <div className="mt-1.5 font-display font-extrabold text-navy-deep">{d.nameEn}</div>
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                  <ShieldCheck className="h-3 w-3" /> {d.authority}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Primary Use Cases</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {p.useCases.map((u) => (
              <li key={u} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white p-3.5 text-sm text-slate-800">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {u}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Full Workflow</div>
          <h2 className="mt-1 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">How to obtain {p.councilEn} certification — 5 steps</h2>
          <ol className="mt-5 space-y-3">
            {[
              { t: "Consultation & assessment", d: "Send licence + ID + destination details; we provide a complete quote (council fee + translation + Notary + MFA + embassy) within 30 minutes via LINE." },
              { t: `${p.councilEn} filing`, d: "Sign the power of attorney; NYC files the Certificate of Good Standing application and pays the council fee on your behalf." },
              { t: "Certified translation", d: "English translation by MoJ-registered translator or NAATI-accredited Thai-English translator (Australia). THB 1,300–2,500 per page." },
              { t: "Notary + MFA legalisation", d: "Notarial Services Attorney (registered with the Lawyers Council of Thailand) attests the translation; Department of Consular Affairs (MFA) legalises the document." },
              { t: "Embassy attestation + courier", d: "Destination embassy attestation (if required), then DHL/FedEx (THB 2,500 worldwide) or EMS (THB 100 domestic)." },
            ].map((s, i) => (
              <li key={i} className="flex gap-4 rounded-xl border border-amber-200 bg-white p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-lg font-extrabold text-amber-700">{i + 1}</div>
                <div>
                  <div className="font-display font-extrabold text-navy-deep">{s.t}</div>
                  <div className="mt-1 text-[13px] leading-relaxed text-slate-700">{s.d}</div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12 rounded-3xl border border-slate-200 bg-slate-50/60 p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Pricing</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            {p.nameEn} package — from THB {p.priceFromTh.toLocaleString()}
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Delivery: {p.turnaround.replace("วันทำการ", "business days")}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Includes translation + Notary + council filing</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> MFA legalisation: +THB 2,500–5,000</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Embassy attestation: +THB 5,000–12,000</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> EMS domestic THB 100 / DHL worldwide THB 2,500</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Tax invoice & instalment available</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other Professional Councils</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to="/en/medical-council/$profession" params={{ profession: r.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">{r.councilEn}</div>
                <div className="mt-1.5 font-display font-extrabold text-navy-deep group-hover:text-amber-700">{r.nameEn}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">From THB {r.priceFromTh.toLocaleString()}</div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-14 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          <h2 className="font-display text-xl font-extrabold text-navy-deep sm:text-2xl">Authority References</h2>
          <ul className="mt-3 list-disc space-y-1.5 pl-5 text-[13px] text-slate-700">
            <li><a href={p.councilUrl} target="_blank" rel="noopener noreferrer nofollow" className="text-amber-700 underline-offset-2 hover:underline">{p.councilEn}</a></li>
            <li><a href="https://www.consular.mfa.go.th/en" target="_blank" rel="noopener noreferrer nofollow" className="text-amber-700 underline-offset-2 hover:underline">Department of Consular Affairs, MFA Thailand</a></li>
            <li><a href="https://www.lawyerscouncil.or.th" target="_blank" rel="noopener noreferrer nofollow" className="text-amber-700 underline-offset-2 hover:underline">Lawyers Council of Thailand (Notary Public)</a></li>
          </ul>
        </section>
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
